import { useState, useRef, useEffect, type KeyboardEvent } from "react";
import { cn } from "../lib/utils";

export interface MentionSuggestion {
  id: string;
  label: string;
  description?: string;
  avatar?: string;
}

interface MentionInputProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  suggestions?: MentionSuggestion[];
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  className?: string;
}

export function MentionInput({
  value,
  defaultValue = "",
  onChange,
  suggestions = [],
  placeholder = "Type @ to mention someone...",
  disabled,
  rows = 3,
  className,
}: MentionInputProps) {
  const controlled = value !== undefined;
  const [internal, setInternal] = useState(defaultValue);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const atPos = useRef<number>(-1);

  const text = controlled ? value! : internal;

  function update(next: string) {
    if (!controlled) setInternal(next);
    onChange?.(next);
  }

  const filtered = query
    ? suggestions.filter(s => s.label.toLowerCase().includes(query.toLowerCase()))
    : suggestions;

  function handleInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const val = e.target.value;
    const caret = e.target.selectionStart ?? val.length;

    // find the @ before caret
    const slice = val.slice(0, caret);
    const atIdx = slice.lastIndexOf("@");
    if (atIdx !== -1 && !slice.slice(atIdx + 1).includes(" ")) {
      atPos.current = atIdx;
      setQuery(slice.slice(atIdx + 1));
      setOpen(true);
      setIdx(0);
    } else {
      setOpen(false);
      atPos.current = -1;
    }
    update(val);
  }

  function pick(s: MentionSuggestion) {
    const ta = taRef.current;
    if (!ta || atPos.current === -1) return;
    const before = text.slice(0, atPos.current);
    const after = text.slice(ta.selectionStart ?? text.length);
    const next = `${before}@${s.label} ${after}`;
    update(next);
    setOpen(false);
    atPos.current = -1;
    // move caret after insertion
    const pos = before.length + s.label.length + 2;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (!open || !filtered.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx(i => (i + 1) % filtered.length); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setIdx(i => (i - 1 + filtered.length) % filtered.length); }
    if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(filtered[idx]); }
    if (e.key === "Escape") { setOpen(false); }
  }

  // close on outside click
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function h(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={wrapRef} className={cn("mention-wrap", className)}>
      <textarea
        ref={taRef}
        className="mention-textarea"
        value={text}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        aria-autocomplete="list"
        aria-expanded={open}
      />
      {open && filtered.length > 0 && (
        <ul className="mention-list" role="listbox">
          {filtered.map((s, i) => (
            <li
              key={s.id}
              role="option"
              aria-selected={i === idx}
              className={cn("mention-item", i === idx && "mention-item-active")}
              onMouseDown={e => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setIdx(i)}
            >
              {s.avatar
                ? <img src={s.avatar} alt="" className="mention-avatar" />
                : <span className="mention-avatar mention-avatar-fallback">{s.label[0].toUpperCase()}</span>
              }
              <div className="mention-info">
                <span className="mention-label">{s.label}</span>
                {s.description && <span className="mention-desc">{s.description}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
